import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { assessmentsAPI, challengesAPI, adLabsAPI } from '../../utils/api';
import Button from '../shared/Button';
import CandidateSelector from '../shared/CandidateSelector';
import JobPostingSelector from '../shared/JobPostingSelector';
import './AssessmentCreateWizard.css';

/**
 * AssessmentCreateWizard Component
 *
 * Clean, guided 4-step wizard for creating assessments
 * Following UX best practices - progressive disclosure, clear steps, minimal cognitive load
 */
const AssessmentCreateWizard = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Data from API
  const [challenges, setChallenges] = useState([]);
  const [scenarios, setScenarios] = useState([
    { key: 'ad-lateral-movement', label: 'AD Lateral Movement', description: 'Kerberoasting, lateral move to file server, cron hijack, domain takeover' },
    { key: 'ad-kerberoasting', label: 'AD Kerberoasting', description: 'Enumerate SPNs, request TGS tickets, crack hashes offline, abuse ACLs to reach Domain Admin' },
    { key: 'hollow-crown', label: 'Hollow Crown', description: 'Assumed breach — multi-stage pivot across 3 VLANs to Domain Admin (AS-REP roast → MSSQL → LSA → WinRM → DA)', difficulty: 'hard' }
  ]);

  // Form data
  const [formData, setFormData] = useState({
    // Step 1: Basic Info
    name: '',
    position: '',
    jobPostingId: '', // Linked job posting
    candidateId: '',
    candidateName: '', // Store for review display
    candidateEmail: '', // Store for review display

    // Step 2: Mode & Scheduling
    mode: 'async',
    scheduledAt: '',
    availableFrom: '',
    availableUntil: '',
    timeLimit: 7200, // 2 hours in seconds

    // Step 3: Challenges / Lab
    selectedChallenges: [],
    labType: 'ctf', // 'ctf' | 'red-team'
    scenarios: [], // red-team scenario keys (multi-select)

    // Auto-populated
    interviewerId: null,
    assignmentMode: 'none',
    hints: {
      enabled: true,
      maxPerChallenge: 3,
      penaltyPerHint: 5
    },
    scoring: {
      method: 'automated',
      passThreshold: 70
    }
  });

  const [validationErrors, setValidationErrors] = useState({});

  // Load data on mount
  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError('');

      // Load active challenges and red-team scenarios in parallel
      const [challengesRes, scenariosRes] = await Promise.allSettled([
        challengesAPI.getAll({ isActive: true, availableOnly: true }),
        adLabsAPI.listScenarios()
      ]);

      if (challengesRes.status === 'fulfilled') {
        setChallenges(challengesRes.value.data.challenges || []);
      }
      if (scenariosRes.status === 'fulfilled') {
        const list = scenariosRes.value.data;
        if (Array.isArray(list) && list.length > 0) setScenarios(list);
      }

    } catch (err) {
      console.error('[AssessmentWizard] Failed to load data:', err);
      setError('Failed to load required data. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  // Handle form field changes
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Handle challenge selection
  const toggleChallenge = (challengeId) => {
    setFormData(prev => {
      const isSelected = prev.selectedChallenges.includes(challengeId);
      return {
        ...prev,
        selectedChallenges: isSelected
          ? prev.selectedChallenges.filter(id => id !== challengeId)
          : [...prev.selectedChallenges, challengeId]
      };
    });
  };

  // Validate current step
  const validateStep = (step) => {
    const errors = {};

    if (step === 1) {
      if (!formData.name.trim()) errors.name = 'Assessment name is required';
      if (!formData.position.trim() && !formData.jobPostingId) errors.position = 'Position is required';
      if (!formData.candidateId) errors.candidateId = 'Please select a candidate';
    }

    if (step === 2) {
      if (formData.mode === 'live' && !formData.scheduledAt) {
        errors.scheduledAt = 'Schedule date/time is required for live interviews';
      }
      if (formData.mode !== 'live') {
        if (!formData.availableFrom) errors.availableFrom = 'Start date is required';
        if (!formData.availableUntil) errors.availableUntil = 'End date is required';
      }
    }

    if (step === 3) {
      if (formData.labType === 'red-team' && formData.scenarios.length === 0) {
        errors.scenarios = 'Please select at least one scenario';
      }
      if (formData.labType !== 'red-team' && formData.selectedChallenges.length === 0) {
        errors.challenges = 'Please select at least one challenge';
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Next step
  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => prev + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Previous step
  const handleBack = () => {
    setCurrentStep(prev => prev - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Submit assessment
  const handleSubmit = async () => {
    if (!validateStep(3)) return;

    setSubmitting(true);
    setError('');

    try {
      // Prepare payload
      const payload = {
        name: formData.name,
        position: formData.position,
        jobPostingId: formData.jobPostingId || null,
        candidateId: formData.candidateId,
        interviewerId: formData.interviewerId,
        assignmentMode: formData.assignmentMode,
        mode: formData.mode,
        scheduling: {
          scheduledAt: formData.scheduledAt || null,
          availableFrom: formData.availableFrom || null,
          availableUntil: formData.availableUntil || null,
          timeLimit: formData.timeLimit,
          allowPause: formData.mode === 'async'
        },
        challenges: formData.labType === 'red-team'
          ? { selectionMode: 'red-team', scenarios: formData.scenarios, challengeIds: [] }
          : { selectionMode: 'manual', challengeIds: formData.selectedChallenges },
        hints: formData.hints,
        scoring: formData.scoring,
        status: formData.scheduledAt ? 'scheduled' : 'active'
      };

      console.log('[AssessmentWizard] Creating assessment:', payload);

      const response = await assessmentsAPI.create(payload);

      console.log('[AssessmentWizard] Assessment created successfully:', response.data);

      // Redirect to assessment details
      const assessment = response.data.assessment;
      const assessmentId = assessment._id?.toString() || assessment.id?.toString();

      if (!assessmentId) {
        console.error('[AssessmentWizard] No valid assessment ID in response:', assessment);
        throw new Error('Assessment created but no valid ID returned');
      }

      console.log('[AssessmentWizard] Navigating to assessment:', assessmentId);
      navigate(`/recruiter/assessments/${assessmentId}`);

    } catch (err) {
      console.error('[AssessmentWizard] Failed to create assessment:', err);
      setError(err.response?.data?.error || 'Failed to create assessment. Please try again.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="wizard-container">
        <div className="loading-state">
          <div className="cyber-spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="wizard-container">
      <div className="wizard-header">
        <h1>Create New Assessment</h1>
        <p className="wizard-subtitle">Set up a security assessment for your candidate</p>
      </div>

      {/* Progress Stepper */}
      <div className="wizard-stepper">
        <div className={`step ${currentStep >= 1 ? 'active' : ''} ${currentStep > 1 ? 'completed' : ''}`}>
          <div className="step-number">{currentStep > 1 ? '✓' : '1'}</div>
          <div className="step-label">Basic Info</div>
        </div>
        <div className={`step-line ${currentStep > 1 ? 'completed' : ''}`}></div>
        <div className={`step ${currentStep >= 2 ? 'active' : ''} ${currentStep > 2 ? 'completed' : ''}`}>
          <div className="step-number">{currentStep > 2 ? '✓' : '2'}</div>
          <div className="step-label">Scheduling</div>
        </div>
        <div className={`step-line ${currentStep > 2 ? 'completed' : ''}`}></div>
        <div className={`step ${currentStep >= 3 ? 'active' : ''} ${currentStep > 3 ? 'completed' : ''}`}>
          <div className="step-number">{currentStep > 3 ? '✓' : '3'}</div>
          <div className="step-label">Challenges</div>
        </div>
        <div className={`step-line ${currentStep > 3 ? 'completed' : ''}`}></div>
        <div className={`step ${currentStep >= 4 ? 'active' : ''}`}>
          <div className="step-number">4</div>
          <div className="step-label">Review</div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="notification error-notification">
          <span className="notification-message">{error}</span>
        </div>
      )}

      {/* Step Content */}
      <div className="wizard-content">
        {/* STEP 1: Basic Information */}
        {currentStep === 1 && (
          <div className="wizard-step">
            <h2>Basic Information</h2>
            <p className="step-description">Who is this assessment for and what position?</p>

            <div className="form-group">
              <label htmlFor="name">Assessment Name *</label>
              <input
                type="text"
                id="name"
                className={`form-input ${validationErrors.name ? 'error' : ''}`}
                placeholder="e.g., Senior Security Engineer - Technical Screen"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
              />
              {validationErrors.name && <span className="error-message">{validationErrors.name}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="position">Position *</label>
              <JobPostingSelector
                value={formData.jobPostingId}
                onChange={(jobPostingId) => handleChange('jobPostingId', jobPostingId)}
                onSelectJobPosting={(posting) => {
                  handleChange('position', posting.title);
                  handleChange('jobPostingId', posting._id);
                }}
                error={validationErrors.position}
              />
              {validationErrors.position && <span className="error-message">{validationErrors.position}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="candidateId">Select Candidate *</label>
              <CandidateSelector
                value={formData.candidateId}
                onChange={(candidateId) => handleChange('candidateId', candidateId)}
                onSelectCandidate={(candidate) => {
                  handleChange('candidateName', candidate.name);
                  handleChange('candidateEmail', candidate.email);
                }}
                error={validationErrors.candidateId}
              />
            </div>
          </div>
        )}

        {/* STEP 2: Mode & Scheduling */}
        {currentStep === 2 && (
          <div className="wizard-step">
            <h2>Assessment Mode & Scheduling</h2>
            <p className="step-description">When and how will the assessment be conducted?</p>

            {/* Mode Selection */}
            <div className="form-group">
              <label>Assessment Mode *</label>
              <div className="mode-cards">
                <label className={`mode-card ${formData.mode === 'live' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="mode"
                    value="live"
                    checked={formData.mode === 'live'}
                    onChange={(e) => handleChange('mode', e.target.value)}
                  />
                  <div className="mode-card-content">
                    <h3>Live Interview</h3>
                    <p>Real-time with interviewer</p>
                  </div>
                </label>

                <label className={`mode-card ${formData.mode === 'async' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="mode"
                    value="async"
                    checked={formData.mode === 'async'}
                    onChange={(e) => handleChange('mode', e.target.value)}
                  />
                  <div className="mode-card-content">
                    <h3>Async</h3>
                    <p>Self-paced, candidate's own time</p>
                  </div>
                </label>

                <label className={`mode-card ${formData.mode === 'timed' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="mode"
                    value="timed"
                    checked={formData.mode === 'timed'}
                    onChange={(e) => handleChange('mode', e.target.value)}
                  />
                  <div className="mode-card-content">
                    <h3>Timed</h3>
                    <p>Time-boxed, once started</p>
                  </div>
                </label>
              </div>
            </div>

            {/* Scheduling Fields */}
            {formData.mode === 'live' ? (
              <div className="form-group">
                <label htmlFor="scheduledAt">Schedule Date & Time *</label>
                <input
                  type="datetime-local"
                  id="scheduledAt"
                  className={`form-input ${validationErrors.scheduledAt ? 'error' : ''}`}
                  value={formData.scheduledAt}
                  onChange={(e) => handleChange('scheduledAt', e.target.value)}
                />
                {validationErrors.scheduledAt && <span className="error-message">{validationErrors.scheduledAt}</span>}
              </div>
            ) : (
              <>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="availableFrom">Available From *</label>
                    <input
                      type="datetime-local"
                      id="availableFrom"
                      className={`form-input ${validationErrors.availableFrom ? 'error' : ''}`}
                      value={formData.availableFrom}
                      onChange={(e) => handleChange('availableFrom', e.target.value)}
                    />
                    {validationErrors.availableFrom && <span className="error-message">{validationErrors.availableFrom}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="availableUntil">Available Until *</label>
                    <input
                      type="datetime-local"
                      id="availableUntil"
                      className={`form-input ${validationErrors.availableUntil ? 'error' : ''}`}
                      value={formData.availableUntil}
                      onChange={(e) => handleChange('availableUntil', e.target.value)}
                    />
                    {validationErrors.availableUntil && <span className="error-message">{validationErrors.availableUntil}</span>}
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="timeLimit">Time Limit (once started)</label>
                  <select
                    id="timeLimit"
                    className="form-input"
                    value={formData.timeLimit}
                    onChange={(e) => handleChange('timeLimit', parseInt(e.target.value))}
                  >
                    <option value={1800}>30 minutes</option>
                    <option value={3600}>1 hour</option>
                    <option value={5400}>1.5 hours</option>
                    <option value={7200}>2 hours</option>
                    <option value={10800}>3 hours</option>
                    <option value={14400}>4 hours</option>
                  </select>
                </div>
              </>
            )}
          </div>
        )}

        {/* STEP 3: Challenge Selection */}
        {currentStep === 3 && (
          <div className="wizard-step">
            <h2>Select Lab Type</h2>
            <p className="step-description">Choose what the candidate will complete</p>

            {/* Lab type toggle */}
            <div className="mode-cards" style={{ marginBottom: '1.5rem' }}>
              <label className={`mode-card ${formData.labType === 'ctf' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="labType"
                  value="ctf"
                  checked={formData.labType === 'ctf'}
                  onChange={() => handleChange('labType', 'ctf')}
                />
                <div className="mode-card-content">
                  <h3>CTF Challenges</h3>
                  <p>Web, SQL injection, XSS — isolated Docker labs</p>
                </div>
              </label>

              <label className={`mode-card ${formData.labType === 'red-team' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="labType"
                  value="red-team"
                  checked={formData.labType === 'red-team'}
                  onChange={() => handleChange('labType', 'red-team')}
                />
                <div className="mode-card-content">
                  <h3>Red Team Lab</h3>
                  <p>Full environment — AD, network pivot, and more scenarios</p>
                </div>
              </label>
            </div>

            {/* CTF challenge picker */}
            {formData.labType !== 'red-team' && (
              <>
                {validationErrors.challenges && (
                  <div className="notification error-notification">
                    <span className="notification-message">{validationErrors.challenges}</span>
                  </div>
                )}

                <div className="challenges-grid">
                  {challenges.map(challenge => (
                    <label
                      key={challenge._id}
                      className={`challenge-card ${formData.selectedChallenges.includes(challenge._id) ? 'selected' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={formData.selectedChallenges.includes(challenge._id)}
                        onChange={() => toggleChallenge(challenge._id)}
                      />
                      <div className="challenge-card-content">
                        <h3>{challenge.title}</h3>
                        <p className="challenge-category">{challenge.category}</p>
                        <div className="challenge-meta">
                          <span className={`difficulty-badge difficulty-${challenge.difficulty?.toLowerCase()}`}>
                            {challenge.difficulty}
                          </span>
                          <span className="points-badge">{challenge.totalPoints || 100} pts</span>
                        </div>
                      </div>
                      {formData.selectedChallenges.includes(challenge._id) && (
                        <div className="selected-indicator">✓</div>
                      )}
                    </label>
                  ))}
                </div>

                {challenges.length === 0 && (
                  <div className="empty-state">
                    <p>No active challenges found. Please create challenges first.</p>
                  </div>
                )}

                <div className="selection-summary">
                  <strong>{formData.selectedChallenges.length}</strong> challenge{formData.selectedChallenges.length !== 1 ? 's' : ''} selected
                </div>
              </>
            )}

            {/* Red Team Lab scenario picker */}
            {formData.labType === 'red-team' && (
              <div className="details-card" style={{ background: '#0d1117', border: '1px solid #1e293b', borderRadius: '8px', padding: '1.25rem' }}>
                <h3 style={{ color: '#f87171', marginBottom: '0.75rem' }}>Select Scenarios</h3>
                <p style={{ color: '#94a3b8', fontSize: '0.875rem', marginBottom: '1rem' }}>
                  Each selected scenario gets its own lab environment, provisioned from Assessment Details.
                </p>
                {validationErrors.scenarios && (
                  <div className="notification error-notification" style={{ marginBottom: '0.75rem' }}>
                    <span className="notification-message">{validationErrors.scenarios}</span>
                  </div>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {scenarios.map(s => {
                    const checked = formData.scenarios.includes(s.key);
                    return (
                      <label
                        key={s.key}
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '0.75rem',
                          padding: '0.75rem',
                          background: checked ? '#1e2a3a' : '#111827',
                          border: checked ? '1px solid #3b82f6' : '1px solid #1e293b',
                          borderRadius: '6px',
                          cursor: 'pointer'
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            const next = checked
                              ? formData.scenarios.filter(k => k !== s.key)
                              : [...formData.scenarios, s.key];
                            handleChange('scenarios', next);
                          }}
                          style={{ marginTop: '2px' }}
                        />
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <span style={{ color: '#e2e8f0', fontSize: '0.9rem', fontWeight: '600' }}>{s.label}</span>
                            {s.difficulty && (
                              <span style={{
                                fontSize: '0.7rem', fontWeight: '700', padding: '1px 6px',
                                borderRadius: '3px', textTransform: 'uppercase', letterSpacing: '0.05em',
                                background: s.difficulty === 'hard' ? '#7f1d1d' : s.difficulty === 'easy' ? '#14532d' : '#1e3a5f',
                                color: s.difficulty === 'hard' ? '#fca5a5' : s.difficulty === 'easy' ? '#86efac' : '#93c5fd'
                              }}>{s.difficulty}</span>
                            )}
                          </div>
                          <div style={{ color: '#64748b', fontSize: '0.8rem', marginTop: '2px' }}>{s.description}</div>
                        </div>
                      </label>
                    );
                  })}
                </div>
                <div style={{ marginTop: '0.75rem', color: '#94a3b8', fontSize: '0.85rem' }}>
                  <strong style={{ color: '#e2e8f0' }}>{formData.scenarios.length}</strong> scenario{formData.scenarios.length !== 1 ? 's' : ''} selected
                </div>
              </div>
            )}
          </div>
        )}

        {/* STEP 4: Review */}
        {currentStep === 4 && (
          <div className="wizard-step">
            <h2>Review & Create</h2>
            <p className="step-description">Review your assessment configuration</p>

            <div className="review-sections">
              <div className="review-section">
                <h3>Basic Information</h3>
                <div className="review-item">
                  <label>Assessment Name:</label>
                  <span>{formData.name}</span>
                </div>
                <div className="review-item">
                  <label>Position:</label>
                  <span>{formData.position}</span>
                </div>
                <div className="review-item">
                  <label>Candidate:</label>
                  <span>
                    {formData.candidateName && formData.candidateEmail
                      ? `${formData.candidateName} (${formData.candidateEmail})`
                      : formData.candidateName
                      ? formData.candidateName
                      : formData.candidateEmail
                      ? formData.candidateEmail
                      : 'Not yet loaded'}
                  </span>
                </div>
              </div>

              <div className="review-section">
                <h3>Scheduling</h3>
                <div className="review-item">
                  <label>Mode:</label>
                  <span className="mode-badge">{formData.mode.toUpperCase()}</span>
                </div>
                {formData.mode === 'live' ? (
                  <div className="review-item">
                    <label>Scheduled:</label>
                    <span>{formData.scheduledAt ? new Date(formData.scheduledAt).toLocaleString() : 'Not set'}</span>
                  </div>
                ) : (
                  <>
                    <div className="review-item">
                      <label>Available:</label>
                      <span>
                        {formData.availableFrom && formData.availableUntil
                          ? `${new Date(formData.availableFrom).toLocaleString()} to ${new Date(formData.availableUntil).toLocaleString()}`
                          : 'Not set'}
                      </span>
                    </div>
                    <div className="review-item">
                      <label>Time Limit:</label>
                      <span>{(formData.timeLimit / 3600).toFixed(1)} hour{formData.timeLimit !== 3600 ? 's' : ''}</span>
                    </div>
                  </>
                )}
              </div>

              <div className="review-section">
                {formData.labType === 'red-team' ? (
                  <>
                    <h3>Red Team Lab ({formData.scenarios.length} scenario{formData.scenarios.length !== 1 ? 's' : ''})</h3>
                    <div className="review-item">
                      <label>Scenarios:</label>
                      <span>
                        {formData.scenarios.map(key => scenarios.find(s => s.key === key)?.label || key).join(', ')}
                      </span>
                    </div>
                    <div className="review-item">
                      <label>Environment:</label>
                      <span>Each scenario provisioned on start from Assessment Details</span>
                    </div>
                  </>
                ) : (
                  <>
                    <h3>Challenges ({formData.selectedChallenges.length})</h3>
                    <ul className="review-challenges">
                      {formData.selectedChallenges.map(id => {
                        const challenge = challenges.find(c => c._id === id);
                        return challenge ? (
                          <li key={id}>
                            {challenge.title}
                            <span className={`difficulty-badge difficulty-${challenge.difficulty?.toLowerCase()}`}>
                              {challenge.difficulty}
                            </span>
                          </li>
                        ) : null;
                      })}
                    </ul>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Navigation Buttons */}
      <div className="wizard-actions">
        <Button
          variant="secondary"
          onClick={() => currentStep === 1 ? navigate('/recruiter/assessments') : handleBack()}
        >
          {currentStep === 1 ? 'Cancel' : 'Back'}
        </Button>

        <div className="wizard-actions-right">
          {currentStep < 4 ? (
            <Button variant="primary" onClick={handleNext}>
              Next
            </Button>
          ) : (
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? 'Creating...' : 'Create Assessment'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssessmentCreateWizard;
